export type MagicItemRarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'very-rare'
  | 'legendary'
  | 'artifact';

export type MagicItemType =
  | 'armor'
  | 'potion'
  | 'ring'
  | 'rod'
  | 'scroll'
  | 'staff'
  | 'wand'
  | 'weapon'
  | 'wondrous-item';

export interface MagicItem {
  id: string;
  name: string;
  type: MagicItemType;
  rarity: MagicItemRarity;
  attunement: boolean;
  attunementRequirement?: string;
  description: string;
  source: 'srd-5.1';
}

/**
 * SRD 5.1 Magic Items
 * A selection of magic items from the System Reference Document
 */
export const magicItems: MagicItem[] = [
  // Potions
  {
    id: 'potion-of-healing',
    name: 'Potion of Healing',
    type: 'potion',
    rarity: 'common',
    attunement: false,
    description: 'You regain 2d4 + 2 hit points when you drink this potion. The potion\'s red liquid glimmers when agitated.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-greater-healing',
    name: 'Potion of Greater Healing',
    type: 'potion',
    rarity: 'uncommon',
    attunement: false,
    description: 'You regain 4d4 + 4 hit points when you drink this potion.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-superior-healing',
    name: 'Potion of Superior Healing',
    type: 'potion',
    rarity: 'rare',
    attunement: false,
    description: 'You regain 8d4 + 8 hit points when you drink this potion.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-supreme-healing',
    name: 'Potion of Supreme Healing',
    type: 'potion',
    rarity: 'very-rare',
    attunement: false,
    description: 'You regain 10d4 + 20 hit points when you drink this potion.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-climbing',
    name: 'Potion of Climbing',
    type: 'potion',
    rarity: 'common',
    attunement: false,
    description: 'When you drink this potion, you gain a climbing speed equal to your walking speed for 1 hour. During this time, you have advantage on Strength (Athletics) checks you make to climb.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-heroism',
    name: 'Potion of Heroism',
    type: 'potion',
    rarity: 'rare',
    attunement: false,
    description: 'For 1 hour after drinking it, you gain 10 temporary hit points that last for 1 hour. For the same duration, you are under the effect of the bless spell (no concentration required).',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-invisibility',
    name: 'Potion of Invisibility',
    type: 'potion',
    rarity: 'very-rare',
    attunement: false,
    description: 'This potion\'s container looks empty but feels as though it holds liquid. When you drink it, you become invisible for 1 hour. Anything you wear or carry is invisible with you. The effect ends early if you attack or cast a spell.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-resistance',
    name: 'Potion of Resistance',
    type: 'potion',
    rarity: 'uncommon',
    attunement: false,
    description: 'When you drink this potion, you gain resistance to one type of damage for 1 hour. The DM chooses the type or determines it randomly.',
    source: 'srd-5.1',
  },
  {
    id: 'potion-of-speed',
    name: 'Potion of Speed',
    type: 'potion',
    rarity: 'very-rare',
    attunement: false,
    description: 'When you drink this potion, you gain the effect of the haste spell for 1 minute (no concentration required).',
    source: 'srd-5.1',
  },

  // Armor
  {
    id: 'armor-plus-1',
    name: 'Armor, +1',
    type: 'armor',
    rarity: 'rare',
    attunement: false,
    description: 'You have a +1 bonus to AC while wearing this armor.',
    source: 'srd-5.1',
  },
  {
    id: 'armor-plus-2',
    name: 'Armor, +2',
    type: 'armor',
    rarity: 'very-rare',
    attunement: false,
    description: 'You have a +2 bonus to AC while wearing this armor.',
    source: 'srd-5.1',
  },
  {
    id: 'armor-plus-3',
    name: 'Armor, +3',
    type: 'armor',
    rarity: 'legendary',
    attunement: false,
    description: 'You have a +3 bonus to AC while wearing this armor.',
    source: 'srd-5.1',
  },
  {
    id: 'armor-of-resistance',
    name: 'Armor of Resistance',
    type: 'armor',
    rarity: 'rare',
    attunement: true,
    description: 'You have resistance to one type of damage while you wear this armor. The DM chooses the type or determines it randomly.',
    source: 'srd-5.1',
  },
  {
    id: 'mithral-armor',
    name: 'Mithral Armor',
    type: 'armor',
    rarity: 'uncommon',
    attunement: false,
    description: 'Mithral is a light, flexible metal. A mithral chain shirt or breastplate can be worn under normal clothes. If the armor normally imposes disadvantage on Dexterity (Stealth) checks or has a Strength requirement, the mithral version of the armor doesn\'t.',
    source: 'srd-5.1',
  },
  {
    id: 'adamantine-armor',
    name: 'Adamantine Armor',
    type: 'armor',
    rarity: 'uncommon',
    attunement: false,
    description: 'This suit of armor is reinforced with adamantine, one of the hardest substances in existence. While you\'re wearing it, any critical hit against you becomes a normal hit.',
    source: 'srd-5.1',
  },

  // Weapons
  {
    id: 'weapon-plus-1',
    name: 'Weapon, +1',
    type: 'weapon',
    rarity: 'uncommon',
    attunement: false,
    description: 'You have a +1 bonus to attack and damage rolls made with this magic weapon.',
    source: 'srd-5.1',
  },
  {
    id: 'weapon-plus-2',
    name: 'Weapon, +2',
    type: 'weapon',
    rarity: 'rare',
    attunement: false,
    description: 'You have a +2 bonus to attack and damage rolls made with this magic weapon.',
    source: 'srd-5.1',
  },
  {
    id: 'weapon-plus-3',
    name: 'Weapon, +3',
    type: 'weapon',
    rarity: 'very-rare',
    attunement: false,
    description: 'You have a +3 bonus to attack and damage rolls made with this magic weapon.',
    source: 'srd-5.1',
  },
  {
    id: 'flame-tongue',
    name: 'Flame Tongue',
    type: 'weapon',
    rarity: 'rare',
    attunement: true,
    description: 'You can use a bonus action to speak this magic sword\'s command word, causing flames to erupt from the blade. These flames shed bright light in a 40-foot radius and dim light for an additional 40 feet. While the sword is ablaze, it deals an extra 2d6 fire damage to any target it hits. The flames last until you use a bonus action to speak the command word again or until you drop or sheathe the sword.',
    source: 'srd-5.1',
  },
  {
    id: 'frost-brand',
    name: 'Frost Brand',
    type: 'weapon',
    rarity: 'very-rare',
    attunement: true,
    description: 'When you hit with an attack using this magic sword, the target takes an extra 1d6 cold damage. In addition, while you hold the sword, you have resistance to fire damage. In freezing temperatures, the blade sheds bright light in a 10-foot radius and dim light for an additional 10 feet.',
    source: 'srd-5.1',
  },
  {
    id: 'vorpal-sword',
    name: 'Vorpal Sword',
    type: 'weapon',
    rarity: 'legendary',
    attunement: true,
    description: 'You gain a +3 bonus to attack and damage rolls made with this magic weapon. In addition, the weapon ignores resistance to slashing damage. When you attack a creature that has at least one head with this weapon and roll a 20 on the attack roll, you cut off one of the creature\'s heads.',
    source: 'srd-5.1',
  },

  // Rings
  {
    id: 'ring-of-protection',
    name: 'Ring of Protection',
    type: 'ring',
    rarity: 'rare',
    attunement: true,
    description: 'You gain a +1 bonus to AC and saving throws while wearing this ring.',
    source: 'srd-5.1',
  },
  {
    id: 'ring-of-resistance',
    name: 'Ring of Resistance',
    type: 'ring',
    rarity: 'rare',
    attunement: true,
    description: 'You have resistance to one damage type while wearing this ring. The gem in the ring indicates the type.',
    source: 'srd-5.1',
  },
  {
    id: 'ring-of-spell-storing',
    name: 'Ring of Spell Storing',
    type: 'ring',
    rarity: 'rare',
    attunement: true,
    description: 'This ring stores spells cast into it, holding them until the attuned wearer uses them. The ring can store up to 5 levels worth of spells at a time.',
    source: 'srd-5.1',
  },
  {
    id: 'ring-of-invisibility',
    name: 'Ring of Invisibility',
    type: 'ring',
    rarity: 'legendary',
    attunement: true,
    description: 'While wearing this ring, you can turn invisible as an action. Anything you are wearing or carrying is invisible with you. You remain invisible until the ring is removed, until you attack or cast a spell, or until you use a bonus action to become visible again.',
    source: 'srd-5.1',
  },

  // Wondrous Items
  {
    id: 'bag-of-holding',
    name: 'Bag of Holding',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'This bag has an interior space considerably larger than its outside dimensions, roughly 2 feet in diameter at the mouth and 4 feet deep. The bag can hold up to 500 pounds, not exceeding a volume of 64 cubic feet.',
    source: 'srd-5.1',
  },
  {
    id: 'boots-of-elvenkind',
    name: 'Boots of Elvenkind',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'While you wear these boots, your steps make no sound, regardless of the surface you are moving across. You also have advantage on Dexterity (Stealth) checks that rely on moving silently.',
    source: 'srd-5.1',
  },
  {
    id: 'boots-of-speed',
    name: 'Boots of Speed',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: true,
    description: 'While you wear these boots, you can use a bonus action and click the boots\' heels together. If you do, the boots double your walking speed, and any creature that makes an opportunity attack against you has disadvantage on the attack roll. The effect lasts for 10 minutes.',
    source: 'srd-5.1',
  },
  {
    id: 'bracers-of-defense',
    name: 'Bracers of Defense',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: true,
    description: 'While wearing these bracers, you gain a +2 bonus to AC if you are wearing no armor and using no shield.',
    source: 'srd-5.1',
  },
  {
    id: 'cloak-of-displacement',
    name: 'Cloak of Displacement',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: true,
    description: 'While you wear this cloak, it projects an illusion that makes you appear to be standing in a place near your actual location, causing any creature to have disadvantage on attack rolls against you.',
    source: 'srd-5.1',
  },
  {
    id: 'cloak-of-elvenkind',
    name: 'Cloak of Elvenkind',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'While you wear this cloak with its hood up, Wisdom (Perception) checks made to see you have disadvantage, and you have advantage on Dexterity (Stealth) checks made to hide, as the cloak\'s color shifts to camouflage you.',
    source: 'srd-5.1',
  },
  {
    id: 'cloak-of-protection',
    name: 'Cloak of Protection',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'You gain a +1 bonus to AC and saving throws while you wear this cloak.',
    source: 'srd-5.1',
  },
  {
    id: 'decanter-of-endless-water',
    name: 'Decanter of Endless Water',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'This stoppered flask sloshes when shaken, as if it contains water. The decanter weighs 2 pounds. You can use an action to remove the stopper and speak one of three command words, whereupon an amount of fresh water or salt water pours out of the flask.',
    source: 'srd-5.1',
  },
  {
    id: 'gauntlets-of-ogre-power',
    name: 'Gauntlets of Ogre Power',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'Your Strength score is 19 while you wear these gauntlets. They have no effect on you if your Strength is already 19 or higher.',
    source: 'srd-5.1',
  },
  {
    id: 'gloves-of-missile-snaring',
    name: 'Gloves of Missile Snaring',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'When a ranged weapon attack hits you while you\'re wearing these gloves, you can use your reaction to reduce the damage by 1d10 + your Dexterity modifier, provided that you have a free hand. If you reduce the damage to 0, you can catch the missile if it is small enough for you to hold in that hand.',
    source: 'srd-5.1',
  },
  {
    id: 'goggles-of-night',
    name: 'Goggles of Night',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'While wearing these dark lenses, you have darkvision out to a range of 60 feet. If you already have darkvision, wearing the goggles increases its range by 60 feet.',
    source: 'srd-5.1',
  },
  {
    id: 'handy-haversack',
    name: 'Handy Haversack',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: false,
    description: 'This backpack has a central pouch and two side pouches, each of which is an extradimensional space. Each side pouch can hold up to 20 pounds. The central pouch can hold up to 80 pounds. Retrieving an item from the haversack requires only a bonus action.',
    source: 'srd-5.1',
  },
  {
    id: 'headband-of-intellect',
    name: 'Headband of Intellect',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'Your Intelligence score is 19 while you wear this headband. It has no effect on you if your Intelligence is already 19 or higher.',
    source: 'srd-5.1',
  },
  {
    id: 'helm-of-telepathy',
    name: 'Helm of Telepathy',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'While wearing this helm, you can use an action to cast the detect thoughts spell (save DC 13) from it. As long as you maintain concentration on the spell, you can use a bonus action to send a telepathic message to a creature you are focused on.',
    source: 'srd-5.1',
  },
  {
    id: 'immovable-rod',
    name: 'Immovable Rod',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'This flat iron rod has a button on one end. You can use an action to press the button, which causes the rod to become magically fixed in place. Until you or another creature uses an action to push the button again, the rod doesn\'t move, even if it is defying gravity.',
    source: 'srd-5.1',
  },
  {
    id: 'lantern-of-revealing',
    name: 'Lantern of Revealing',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'While lit, this hooded lantern burns for 6 hours on 1 pint of oil, shedding bright light in a 30-foot radius and dim light for an additional 30 feet. Invisible creatures and objects are visible as long as they are in the lantern\'s bright light.',
    source: 'srd-5.1',
  },
  {
    id: 'medallion-of-thoughts',
    name: 'Medallion of Thoughts',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'The medallion has 3 charges. While wearing it, you can use an action and expend 1 charge to cast the detect thoughts spell (save DC 13) from it. The medallion regains 1d3 expended charges daily at dawn.',
    source: 'srd-5.1',
  },
  {
    id: 'pearl-of-power',
    name: 'Pearl of Power',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    attunementRequirement: 'by a spellcaster',
    description: 'While this pearl is on your person, you can use an action to speak its command word and regain one expended spell slot. If the expended slot was of 4th level or higher, the new slot is 3rd level. Once you use the pearl, it can\'t be used again until the next dawn.',
    source: 'srd-5.1',
  },
  {
    id: 'periapt-of-health',
    name: 'Periapt of Health',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'You are immune to contracting any disease while you wear this pendant. If you are already infected with a disease, the effects of the disease are suppressed while you wear the pendant.',
    source: 'srd-5.1',
  },
  {
    id: 'portable-hole',
    name: 'Portable Hole',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: false,
    description: 'This fine black cloth, soft as silk, is folded up to the dimensions of a handkerchief. It unfolds into a circular sheet 6 feet in diameter. You can use an action to unfold a portable hole and place it on or against a solid surface, whereupon the portable hole creates an extradimensional hole 10 feet deep.',
    source: 'srd-5.1',
  },
  {
    id: 'robe-of-eyes',
    name: 'Robe of Eyes',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: true,
    description: 'This robe is adorned with eyelike patterns. While you wear the robe, you gain darkvision out to 120 feet, you can see invisible creatures and objects, and you can see into the Ethereal Plane. The robe also gives you advantage on sight-based Perception checks.',
    source: 'srd-5.1',
  },
  {
    id: 'rope-of-climbing',
    name: 'Rope of Climbing',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: false,
    description: 'This 60-foot length of silk rope weighs 3 pounds and can hold up to 3,000 pounds. If you hold one end of the rope and use an action to speak the command word, the rope animates.',
    source: 'srd-5.1',
  },
  {
    id: 'slippers-of-spider-climbing',
    name: 'Slippers of Spider Climbing',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'While you wear these light shoes, you can move up, down, and across vertical surfaces and upside down along ceilings, while leaving your hands free. You have a climbing speed equal to your walking speed.',
    source: 'srd-5.1',
  },
  {
    id: 'stone-of-good-luck',
    name: 'Stone of Good Luck (Luckstone)',
    type: 'wondrous-item',
    rarity: 'uncommon',
    attunement: true,
    description: 'While this polished agate is on your person, you gain a +1 bonus to ability checks and saving throws.',
    source: 'srd-5.1',
  },
  {
    id: 'wings-of-flying',
    name: 'Wings of Flying',
    type: 'wondrous-item',
    rarity: 'rare',
    attunement: true,
    description: 'While wearing this cloak, you can use an action to speak its command word. This turns the cloak into a pair of bat wings or bird wings on your back for 1 hour or until you repeat the command word as an action. The wings give you a flying speed of 60 feet.',
    source: 'srd-5.1',
  },

  // Staffs
  {
    id: 'staff-of-fire',
    name: 'Staff of Fire',
    type: 'staff',
    rarity: 'very-rare',
    attunement: true,
    attunementRequirement: 'by a druid, sorcerer, warlock, or wizard',
    description: 'You have resistance to fire damage while you hold this staff. The staff has 10 charges and can cast burning hands (1 charge), fireball (3 charges), and wall of fire (4 charges).',
    source: 'srd-5.1',
  },
  {
    id: 'staff-of-frost',
    name: 'Staff of Frost',
    type: 'staff',
    rarity: 'very-rare',
    attunement: true,
    attunementRequirement: 'by a druid, sorcerer, warlock, or wizard',
    description: 'You have resistance to cold damage while you hold this staff. The staff has 10 charges and can cast cone of cold (5 charges), fog cloud (1 charge), ice storm (4 charges), and wall of ice (4 charges).',
    source: 'srd-5.1',
  },
  {
    id: 'staff-of-healing',
    name: 'Staff of Healing',
    type: 'staff',
    rarity: 'rare',
    attunement: true,
    attunementRequirement: 'by a bard, cleric, or druid',
    description: 'This staff has 10 charges. While holding it, you can use an action to expend 1 or more of its charges to cast cure wounds (1 charge per spell level, up to 4th), lesser restoration (2 charges), or mass cure wounds (5 charges).',
    source: 'srd-5.1',
  },
  {
    id: 'staff-of-power',
    name: 'Staff of Power',
    type: 'staff',
    rarity: 'very-rare',
    attunement: true,
    attunementRequirement: 'by a sorcerer, warlock, or wizard',
    description: 'This staff can be wielded as a magic quarterstaff that grants a +2 bonus to attack and damage rolls made with it. While holding it, you gain a +2 bonus to AC, saving throws, and spell attack rolls. The staff has 20 charges for its other properties.',
    source: 'srd-5.1',
  },
  {
    id: 'staff-of-the-magi',
    name: 'Staff of the Magi',
    type: 'staff',
    rarity: 'legendary',
    attunement: true,
    attunementRequirement: 'by a sorcerer, warlock, or wizard',
    description: 'This staff can be wielded as a magic quarterstaff that grants a +2 bonus to attack and damage rolls made with it. While you hold it, you gain a +2 bonus to spell attack rolls. The staff has 50 charges.',
    source: 'srd-5.1',
  },

  // Wands
  {
    id: 'wand-of-fireballs',
    name: 'Wand of Fireballs',
    type: 'wand',
    rarity: 'rare',
    attunement: true,
    attunementRequirement: 'by a spellcaster',
    description: 'This wand has 7 charges. While holding it, you can use an action to expend 1 or more of its charges to cast the fireball spell (save DC 15) from it.',
    source: 'srd-5.1',
  },
  {
    id: 'wand-of-lightning-bolts',
    name: 'Wand of Lightning Bolts',
    type: 'wand',
    rarity: 'rare',
    attunement: true,
    attunementRequirement: 'by a spellcaster',
    description: 'This wand has 7 charges. While holding it, you can use an action to expend 1 or more of its charges to cast the lightning bolt spell (save DC 15) from it.',
    source: 'srd-5.1',
  },
  {
    id: 'wand-of-magic-detection',
    name: 'Wand of Magic Detection',
    type: 'wand',
    rarity: 'uncommon',
    attunement: false,
    description: 'This wand has 3 charges. While holding it, you can expend 1 charge as an action to cast the detect magic spell from it. The wand regains 1d3 expended charges daily at dawn.',
    source: 'srd-5.1',
  },
  {
    id: 'wand-of-magic-missiles',
    name: 'Wand of Magic Missiles',
    type: 'wand',
    rarity: 'uncommon',
    attunement: false,
    description: 'This wand has 7 charges. While holding it, you can use an action to expend 1 or more of its charges to cast the magic missile spell from it.',
    source: 'srd-5.1',
  },
  {
    id: 'wand-of-secrets',
    name: 'Wand of Secrets',
    type: 'wand',
    rarity: 'uncommon',
    attunement: false,
    description: 'The wand has 3 charges. While holding it, you can use an action to expend 1 of its charges, and if a secret door or trap is within 30 feet of you, the wand pulses and points at the one nearest to you.',
    source: 'srd-5.1',
  },
  {
    id: 'wand-of-web',
    name: 'Wand of Web',
    type: 'wand',
    rarity: 'uncommon',
    attunement: true,
    attunementRequirement: 'by a spellcaster',
    description: 'This wand has 7 charges. While holding it, you can use an action to expend 1 of its charges to cast the web spell (save DC 15) from it.',
    source: 'srd-5.1',
  },

  // Rods
  {
    id: 'immovable-rod',
    name: 'Immovable Rod',
    type: 'rod',
    rarity: 'uncommon',
    attunement: false,
    description: 'This flat iron rod has a button on one end. You can use an action to press the button, which causes the rod to become magically fixed in place.',
    source: 'srd-5.1',
  },
  {
    id: 'rod-of-rulership',
    name: 'Rod of Rulership',
    type: 'rod',
    rarity: 'rare',
    attunement: true,
    description: 'You can use an action to present the rod and command obedience from each creature of your choice that you can see within 120 feet of you. Each target must succeed on a DC 15 Wisdom saving throw or be charmed by you for 8 hours.',
    source: 'srd-5.1',
  },
];

/**
 * Get magic item by ID
 */
export function getMagicItem(id: string): MagicItem | undefined {
  return magicItems.find((item) => item.id === id);
}

/**
 * Get all magic items of a specific type
 */
export function getMagicItemsByType(type: MagicItemType): MagicItem[] {
  return magicItems.filter((item) => item.type === type);
}

/**
 * Get all magic items of a specific rarity
 */
export function getMagicItemsByRarity(rarity: MagicItemRarity): MagicItem[] {
  return magicItems.filter((item) => item.rarity === rarity);
}

/**
 * Get magic items that require attunement
 */
export function getAttunementItems(): MagicItem[] {
  return magicItems.filter((item) => item.attunement);
}

/**
 * Search magic items by name
 */
export function searchMagicItems(query: string): MagicItem[] {
  const normalized = query.toLowerCase().trim();
  return magicItems.filter(
    (item) =>
      item.name.toLowerCase().includes(normalized) ||
      item.id.includes(normalized)
  );
}

/**
 * Rarity display order (for sorting)
 */
export const RARITY_ORDER: MagicItemRarity[] = [
  'common',
  'uncommon',
  'rare',
  'very-rare',
  'legendary',
  'artifact',
];

/**
 * Format rarity for display
 */
export function formatRarity(rarity: MagicItemRarity): string {
  switch (rarity) {
    case 'very-rare':
      return 'Very Rare';
    default:
      return rarity.charAt(0).toUpperCase() + rarity.slice(1);
  }
}
